import { FC, ReactNode } from 'react';
import { cn } from '@new/lib';

interface StackedListProps {
  children: ReactNode;
  className?: string;
}

/**
 * A vertically stacked, divided list built on a composition pattern. Compose a
 * row from the sub-components:
 *
 *   <StackedList>
 *     <StackedList.Item onClick={...}>       // or href={...}, else static
 *       <StackedList.Icon><Avatar/></StackedList.Icon>   // optional
 *       <StackedList.Content>
 *         <StackedList.Label>Title</StackedList.Label>
 *         <StackedList.SubLabel>Subtitle</StackedList.SubLabel>
 *       </StackedList.Content>
 *       <StackedList.Action><Badge/></StackedList.Action> // optional
 *     </StackedList.Item>
 *   </StackedList>
 *
 * Uses design tokens only (no hard-coded colors). Icon and Action are optional.
 */
export const StackedList: FC<StackedListProps> & {
  Item: typeof StackedListItem;
  Icon: typeof StackedListIcon;
  Content: typeof StackedListContent;
  Label: typeof StackedListLabel;
  SubLabel: typeof StackedListSubLabel;
  Action: typeof StackedListAction;
} = ({ children, className }) => (
  <ul role="list" className={cn('divide-y divide-(--neutral-black-NB100)', className)}>
    {children}
  </ul>
);

interface StackedListItemProps {
  children: ReactNode;
  /** Renders an anchor row. */
  href?: string;
  /** Renders a button row. Ignored when `href` is set. */
  onClick?: () => void;
  selected?: boolean;
  className?: string;
}

const ITEM_BASE = 'flex items-center gap-x-4 py-4 px-4';
const INTERACTIVE =
  'w-full text-left transition-colors cursor-pointer hover:bg-(--neutral-white-NW900)';

const StackedListItem: FC<StackedListItemProps> = ({
  children,
  href,
  onClick,
  selected = false,
  className,
}) => {
  const selectedCls = selected ? 'bg-(--primary-O200)/40' : '';

  if (href) {
    return (
      <li>
        <a href={href} className={cn(ITEM_BASE, INTERACTIVE, selectedCls, className)}>
          {children}
        </a>
      </li>
    );
  }

  if (onClick) {
    return (
      <li>
        <button
          type="button"
          onClick={onClick}
          className={cn(ITEM_BASE, INTERACTIVE, selectedCls, className)}
        >
          {children}
        </button>
      </li>
    );
  }

  return <li className={cn(ITEM_BASE, selectedCls, className)}>{children}</li>;
};

const StackedListIcon: FC<{ children: ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <div className={cn('flex size-10 shrink-0 items-center justify-center', className)}>
    {children}
  </div>
);

const StackedListContent: FC<{ children: ReactNode; className?: string }> = ({
  children,
  className,
}) => <div className={cn('flex min-w-0 flex-auto flex-col', className)}>{children}</div>;

const StackedListLabel: FC<{ children: ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <p className={cn('m-0! truncate text-sm font-semibold text-(--neutral-black-NB1000)', className)}>
    {children}
  </p>
);

const StackedListSubLabel: FC<{ children: ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <p className={cn('m-0! mt-1 truncate text-xs text-(--neutral-black-NB600)', className)}>
    {children}
  </p>
);

const StackedListAction: FC<{ children: ReactNode; className?: string }> = ({
  children,
  className,
}) => <div className={cn('flex shrink-0 items-center gap-3', className)}>{children}</div>;

StackedList.Item = StackedListItem;
StackedList.Icon = StackedListIcon;
StackedList.Content = StackedListContent;
StackedList.Label = StackedListLabel;
StackedList.SubLabel = StackedListSubLabel;
StackedList.Action = StackedListAction;
